import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { PiPlus, PiTrash } from 'react-icons/pi';
import { semanticJurisdiction } from '@/lib/helpers';
import { useConfirmation } from '@/hooks/useConfirmation';
import {
  TOffer,
  TOfferItem,
  useOfferServiceCogs,
  useRemoveItem,
} from '@/models/offers';
import { useHasPermission } from '@/models/permissions';
import AddProposalModal from './AddProposalModal';
import AddServiceModal from './AddServiceModal';

type CogsInfo = { cogsAmount: number; cogsEstimated: boolean };

export default function ServicesManager({ offer }: { offer: TOffer }) {
  const isAccepted = offer.accepted;
  const canSeeCogs = useHasPermission('offers:approve-split');
  const removeItem = useRemoveItem();
  // A schedule exists for the current inventory, so changing it will discard the plan.
  // The SR is told before the change, not after (docs/brd/offer.md, step 10 rules).
  const dropsSplitPlan =
    !!offer.splitPlanStatus && offer.splitPlanStatus !== 'cancelled';
  const { confirm, ConfirmationDialog } = useConfirmation();
  const [cpModalOpen, setCpModalOpen] = useState(false);
  const [svcModalOpen, setSvcModalOpen] = useState(false);

  const serviceItems = (offer.items || []).filter(
    (i) => i.itemType === 'service',
  );

  // One batched COGS lookup, lifted here so the standalone-services table can
  // show price + COGS side by side. COGS is BizDev-only (offers:approve-split).
  const cogsInput = useMemo(
    () =>
      serviceItems
        .filter((i) => i.serviceId != null)
        .map((i) => ({
          serviceId: i.serviceId as number,
          priceCurrency: i.currency || 'EUR',
          priceAmount: Number(i.priceAmount) || 0,
        })),
    [serviceItems],
  );
  const { data: cogs } = useOfferServiceCogs(cogsInput, canSeeCogs);
  const cogsByService = useMemo(() => {
    const m = new Map<number, CogsInfo>();
    for (const c of cogs || []) {
      m.set(c.serviceId, {
        cogsAmount: c.cogsAmount,
        cogsEstimated: c.cogsEstimated,
      });
    }
    return m;
  }, [cogs]);

  const handleRemoveItem = async (itemId: number) => {
    const ok = await confirm({
      title: 'Remove Item',
      message: dropsSplitPlan
        ? 'Removing this item discards the approved split payment plan. A new plan will have to be built and sent to BizDev for approval. Remove it?'
        : 'Remove this item from the offer?',
      confirmButtonText: 'Remove',
    });
    if (!ok) return;
    try {
      await removeItem.mutateAsync({
        offerId: offer.id,
        itemId,
        invalidateSplitPlan: dropsSplitPlan,
      });
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `Failed to remove: ${e}`,
      });
    }
  };

  return (
    <Stack gap="md">
      <ConfirmationDialog />
      <AddProposalModal
        dropsSplitPlan={dropsSplitPlan}
        offerId={offer.id}
        opened={cpModalOpen}
        onClose={() => setCpModalOpen(false)}
      />
      <AddServiceModal
        dropsSplitPlan={dropsSplitPlan}
        offerId={offer.id}
        opened={svcModalOpen}
        onClose={() => setSvcModalOpen(false)}
      />

      <Paper withBorder p="sm" data-testid="ux-services-panel">
        <Stack gap="md">
          <CPSection
            items={(offer.items || []).filter((i) => i.itemType === 'proposal')}
            disabled={isAccepted}
            onRemove={handleRemoveItem}
            onAdd={() => setCpModalOpen(true)}
          />
          <ServiceSection
            items={serviceItems}
            cogsByService={cogsByService}
            showCogs={canSeeCogs}
            disabled={isAccepted}
            onRemove={handleRemoveItem}
            onAdd={() => setSvcModalOpen(true)}
          />
        </Stack>
      </Paper>
    </Stack>
  );
}

function CPSection({
  items,
  disabled,
  onRemove,
  onAdd,
}: {
  items: TOfferItem[];
  disabled: boolean;
  onRemove: (id: number) => void;
  onAdd: () => void;
}) {
  return (
    <Box>
      <Group justify="space-between" mb="xs">
        <Text fw={500} fz="sm">
          Commercial Proposals
        </Text>
        {!disabled && (
          <Button
            size="xs"
            variant="light"
            leftSection={<PiPlus size={12} />}
            onClick={onAdd}
          >
            Add CP
          </Button>
        )}
      </Group>
      {items.length === 0 ? (
        <Text fz="sm" c="dimmed" ta="center" py="md">
          No commercial proposals added.
        </Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Jurisdiction</Table.Th>
              <Table.Th>Currency</Table.Th>
              {!disabled && <Table.Th w={40}></Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((item) => (
              <Table.Tr key={item.id}>
                <Table.Td>
                  {item.cpId ? (
                    <Anchor
                      fz="sm"
                      href={`/commercial-proposals/view/${item.cpId}`}
                      target="_blank"
                    >
                      {item.itemName || '—'}
                    </Anchor>
                  ) : (
                    <Text fz="sm">{item.itemName || '—'}</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text fz="xs">
                    {item.jurisdiction
                      ? semanticJurisdiction(item.jurisdiction)
                      : '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text fz="xs">{item.currency || '—'}</Text>
                </Table.Td>
                {!disabled && (
                  <Table.Td>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="red"
                      onClick={() => onRemove(item.id)}
                    >
                      <PiTrash size={14} />
                    </ActionIcon>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Box>
  );
}

function ServiceSection({
  items,
  cogsByService,
  showCogs,
  disabled,
  onRemove,
  onAdd,
}: {
  items: TOfferItem[];
  cogsByService: Map<number, CogsInfo>;
  showCogs: boolean;
  disabled: boolean;
  onRemove: (id: number) => void;
  onAdd: () => void;
}) {
  const hasRenewal = items.some((i) => i.renewal);
  return (
    <Box>
      <Group justify="space-between" mb="xs">
        <Text fw={500} fz="sm">
          Standalone Services
        </Text>
        {!disabled && (
          <Button
            size="xs"
            variant="light"
            leftSection={<PiPlus size={12} />}
            onClick={onAdd}
          >
            Add Service
          </Button>
        )}
      </Group>
      {items.length === 0 ? (
        <Text fz="sm" c="dimmed" ta="center" py="md">
          No standalone services added.
        </Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Industry</Table.Th>
              <Table.Th ta="right">Price</Table.Th>
              {showCogs && <Table.Th ta="right">COGS</Table.Th>}
              {hasRenewal && <Table.Th>Renewal</Table.Th>}
              {!disabled && <Table.Th w={40}></Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((item) => {
              const cogs =
                item.serviceId != null
                  ? cogsByService.get(item.serviceId)
                  : undefined;
              return (
                <Table.Tr key={item.id}>
                  <Table.Td>
                    {item.serviceId ? (
                      <Anchor
                        fz="sm"
                        href={`/products/services/${item.serviceId}`}
                        target="_blank"
                      >
                        {item.itemName || '—'}
                      </Anchor>
                    ) : (
                      <Text fz="sm">{item.itemName || '—'}</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text fz="xs">{item.serviceType || '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz="xs">{item.serviceIndustry || '—'}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text fz="xs">
                      {item.priceAmount != null
                        ? `${Number(item.priceAmount).toLocaleString()} ${item.currency || ''}`
                        : '—'}
                    </Text>
                  </Table.Td>
                  {showCogs && (
                    <Table.Td ta="right">
                      {cogs ? (
                        <Group gap={2} justify="flex-end" wrap="nowrap">
                          <Text fz="xs">
                            {Math.round(cogs.cogsAmount).toLocaleString()}{' '}
                            {item.currency || ''}
                          </Text>
                          {cogs.cogsEstimated && (
                            <Tooltip label="COGS estimated (rate/agent data incomplete)">
                              <Text fz="xs" c="orange">
                                ~
                              </Text>
                            </Tooltip>
                          )}
                        </Group>
                      ) : (
                        <Text fz="xs" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                  )}
                  {hasRenewal && (
                    <Table.Td>
                      <Text fz="xs" tt="capitalize">
                        {item.renewal || ''}
                      </Text>
                    </Table.Td>
                  )}
                  {!disabled && (
                    <Table.Td>
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        onClick={() => onRemove(item.id)}
                      >
                        <PiTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}
    </Box>
  );
}
